import React from 'react';
import { Image } from 'antd';
import { ArrowRight, TrendingUp, TrendingDown, Images, LoaderCircle, ImageOff } from 'lucide-react';
import { StatusTag, EmptyState, CollapsibleCard } from '../../common';
import AdditionalSpaces from './AdditionalSpaces';
import { getPromotorImageUrl, categoryBeforeAfter, usePromotorImages } from './images.controller';

/** One side (Antes / Después) of the before/after comparison. */
function ComparisonSide({ label, labelTone, share, images, rawImages, hasProcessing, alt }) {
    return (
        <div className="rounded-lg border border-surface-200 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-surface-50 border-b border-surface-100">
                <span className={`text-xs font-semibold uppercase tracking-wide ${labelTone === 'success' ? 'text-success-fg' : 'text-danger-fg'}`}>{label}</span>
                <StatusTag tone={labelTone} className="tabular-nums">
                    {!isNaN(share) ? share.toFixed(0) : '--'}%
                </StatusTag>
            </div>

            <div className="p-2">
                {images.length > 0 ? (
                    <Image.PreviewGroup>
                        <div className="space-y-2">
                            {images.slice(0, 2).map((img, imgIdx) => (
                                <Image
                                    key={imgIdx}
                                    src={getPromotorImageUrl(img)}
                                    alt={`${alt} ${imgIdx + 1}`}
                                    width="100%"
                                    height={imgIdx === 0 ? 140 : 80}
                                    className="object-cover rounded-md"
                                    rootClassName="block overflow-hidden rounded-md bg-surface-50"
                                    fallback="/not-found.png"
                                />
                            ))}
                            {images.length > 2 && (
                                <div className="rounded-md bg-surface-100 py-1 text-center text-xs font-medium text-surface-600">+{images.length - 2} más</div>
                            )}
                        </div>
                    </Image.PreviewGroup>
                ) : hasProcessing ? (
                    <div className="flex h-[140px] flex-col items-center justify-center gap-1.5 rounded-md bg-warning-bg text-xs text-warning-fg">
                        <LoaderCircle className="text-lg animate-spin" />
                        <span>Procesando {rawImages.length} foto(s)</span>
                    </div>
                ) : rawImages.length > 0 ? (
                    <div className="flex h-[140px] flex-col items-center justify-center rounded-md bg-warning-bg text-xs text-warning-fg">
                        <span className="font-medium">{rawImages.length} foto(s)</span>
                        <span className="opacity-80">sin URL</span>
                    </div>
                ) : (
                    <div className="flex h-[140px] flex-col items-center justify-center gap-1 rounded-md border border-dashed border-surface-200 text-xs text-surface-400">
                        <ImageOff className="text-base" />
                        Sin foto
                    </div>
                )}
            </div>
        </div>
    );
}

/** Before/after image comparison card for a single category. */
function CategoryBeforeAfterCard({ category }) {
    const {
        shareBefore,
        shareAfter,
        improvement,
        hasImprovement,
        rawImagesBefore,
        rawImagesAfter,
        imagesBefore,
        imagesAfter,
        hasProcessingBefore,
        hasProcessingAfter,
        hasImages
    } = categoryBeforeAfter(category);

    if (!hasImages) return null;

    return (
        <div className="rounded-xl border border-surface-200 h-full flex flex-col">
            {/* Category header with improvement badge */}
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-surface-100">
                <span className="text-sm font-semibold text-surface-900 truncate">{category?.category}</span>
                {hasImprovement ? (
                    <StatusTag tone="success" icon={<TrendingUp />} className="tabular-nums">
                        +{improvement.toFixed(1)}%
                    </StatusTag>
                ) : improvement < 0 ? (
                    <StatusTag tone="danger" icon={<TrendingDown />} className="tabular-nums">
                        {improvement.toFixed(1)}%
                    </StatusTag>
                ) : (
                    <StatusTag tone="neutral">Sin cambio</StatusTag>
                )}
            </div>

            {/* Side-by-side comparison */}
            <div className="p-4 flex-1">
                <div className="grid grid-cols-2 gap-3">
                    <ComparisonSide
                        label="Antes"
                        labelTone="danger"
                        share={shareBefore}
                        images={imagesBefore}
                        rawImages={rawImagesBefore}
                        hasProcessing={hasProcessingBefore}
                        alt={`${category?.category} - Antes`}
                    />
                    <ComparisonSide
                        label="Después"
                        labelTone="success"
                        share={shareAfter}
                        images={imagesAfter}
                        rawImages={rawImagesAfter}
                        hasProcessing={hasProcessingAfter}
                        alt={`${category?.category} - Después`}
                    />
                </div>

                {/* Faces info */}
                <div className="mt-3 flex items-center justify-center gap-3 text-xs text-surface-500 tabular-nums">
                    <span>
                        <span className="font-semibold text-danger-fg">{category?.facesBefore || 0}</span>
                        <span className="mx-1">/</span>
                        <span>{category?.totalBefore || 0} caras</span>
                    </span>
                    <ArrowRight className="text-surface-300" />
                    <span>
                        <span className="font-semibold text-success-fg">{category?.faces || 0}</span>
                        <span className="mx-1">/</span>
                        <span>{category?.totalFaces || 0} caras</span>
                    </span>
                </div>
            </div>
        </div>
    );
}

function PromotorImages({ storeCheck }) {
    const { categoriesWithImages, ourSpaces, theirSpaces, totalImprovement } = usePromotorImages(storeCheck);

    return (
        <CollapsibleCard
            title="Trabajo Realizado"
            icon={<Images />}
            iconTone="brand"
            defaultExpanded // Default expanded for promotor
            badges={
                <>
                    <StatusTag tone="neutral">{categoriesWithImages.length} categorías con fotos</StatusTag>
                    {totalImprovement > 0 && (
                        <StatusTag tone="success" icon={<TrendingUp />} className="tabular-nums">
                            +{totalImprovement.toFixed(1)}% promedio
                        </StatusTag>
                    )}
                </>
            }>
            {categoriesWithImages.length === 0 ? (
                <EmptyState icon={<Images />} title="Sin fotos de antes/después" description="No hay categorías con imágenes de antes/después" />
            ) : (
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                    {categoriesWithImages.map((category, index) => (
                        <CategoryBeforeAfterCard key={index} category={category} />
                    ))}
                </div>
            )}

            <AdditionalSpaces ourSpaces={ourSpaces} theirSpaces={theirSpaces} getImageUrl={getPromotorImageUrl} />
        </CollapsibleCard>
    );
}

export default PromotorImages;
